import { makeTypeObject } from '@apeiron/common'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'

export const useQueryParams = (
  convertType: boolean, // convert string to number or boolean
  scrollToTop: boolean = true
): UseQueryParamResult => {
  const { isReady, query, push } = useRouter()

  // convert query object to nested object
  // eg. { a: '1', b.c: '2' } => { a: 1, b: { c: 2 } }
  const convertObject = (query: ParsedUrlQuery) => {
    const result: any = {}

    for (const key in query) {
      if (key.includes('.')) {
        const keys = key.split('.')

        let current = result

        for (let i = 0; i < keys.length; i++) {
          if (i === keys.length - 1) {
            current[keys[i]] = makeTypeObject(query[key])

            break
          }

          if (!current[keys[i]]) {
            current[keys[i]] = {}
          }

          current = current[keys[i]]
        }
      } else {
        result[key] = makeTypeObject(query[key])
      }
    }

    return result
  }

  const getter = useMemo(() => {
    return convertType ? convertObject(query) : query
  }, [query, convertType])

  const setter: QueryParamSetter = useCallback(
    (...values: any[]) => {
      const searchParams = new URLSearchParams()

      const setQueryParam = (key: string, value: Array<any> | Object) => {
        if (R.isNotNil(value)) {
          if (R.is(Array<any>, value)) {
            value.forEach(v => searchParams.append(key, v))
          } else if (R.is(Object, value)) {
            Object.entries(value).forEach(([nestedKey, nestedValue]: any[]) => {
              setQueryParam(`${key}.${nestedKey}`, nestedValue)
            })
          } else {
            searchParams.set(key, value)
          }
        }
      }

      values.forEach(value => {
        Object.entries(value).forEach(([key, value]: any[]) => {
          setQueryParam(key, value)
        })
      })

      const url = { query: searchParams.toString() }

      const as = undefined

      const options = {
        scroll: scrollToTop
      }

      push(url, as, options)
    },
    [push, scrollToTop]
  )

  const result: UseQueryParamResult = useMemo(
    () => [getter, setter, isReady],
    [getter, setter, isReady]
  )

  return result
}

export const useFakeQueryParams = (
  convertType: boolean = false
): UseQueryParamResult => {
  const [query, setQuery] = useState<ParsedUrlQuery>({})

  const getter = useMemo(() => {
    return convertType ? makeTypeObject(query) : query
  }, [query, convertType])

  const setter: QueryParamSetter = useCallback((...values: any[]) => {
    setQuery(R.mergeAll(values)) // [{ a: 1 }, { b: 2 }] => { a: 1, b: 2 }
  }, [])

  const result: UseQueryParamResult = useMemo(
    () => [getter, setter, true],
    [getter, setter]
  )

  return result
}

export type UseQueryParamResult = [ParsedUrlQuery, QueryParamSetter, boolean]

export type QueryParamSetter = (...values: any[]) => void
